import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ParsedOrder } from "@/lib/bulkOrderUtils";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLanguage } from "@/contexts/LanguageContext";

interface DuplicateComparisonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newOrder: ParsedOrder;
  existingOrder?: ParsedOrder;
}

export function DuplicateComparisonDialog({
  open,
  onOpenChange,
  newOrder,
  existingOrder
}: DuplicateComparisonDialogProps) {
  const { t } = useLanguage();
  
  // Null safety check
  if (!newOrder) {
    return null;
  }

  const isDatabaseDuplicate = existingOrder !== undefined || newOrder.existingOrderData !== undefined;
  const displayExistingOrder = existingOrder || newOrder.existingOrderData;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:w-[95vw] max-w-4xl overflow-x-hidden min-w-0 max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0 min-w-0">
          <DialogTitle>🔄 {t("bulk.duplicate.title")}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-4 min-w-0">
            {/* New Order */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-2 border-b">
                <Badge variant="secondary">{t("bulk.duplicate.newOrder")}</Badge>
                <span className="text-sm text-muted-foreground truncate">
                  {t("bulk.duplicate.fromUpload")}
                </span>
              </div>

              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">{t("bulk.duplicate.orderNumber")}</span> {newOrder.orderNumber || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">{t("bulk.duplicate.customer")}</span> {newOrder.customerName}
                </div>
                <div>
                  <span className="font-medium">{t("bulk.duplicate.phone")}</span> {newOrder.customerPhone || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">{t("bulk.duplicate.address")}</span> {newOrder.address || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">{t("bulk.duplicate.total")}</span> {newOrder.totalPrice ? `${newOrder.totalPrice}₾` : 'N/A'}
                </div>
                <div>
                  <span className="font-medium">{t("bulk.duplicate.products")}</span>
                  <ul className="mt-1 space-y-1 ml-4">
                    {newOrder.products.map((p, idx) => (
                      <li key={idx} className="text-muted-foreground">
                        • {p.name} {p.quantity && `(${p.quantity})`}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Existing Order or Message */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-2 border-b">
                <Badge variant="outline" className="border-orange-500">{t("bulk.duplicate.existing")}</Badge>
                <span className="text-sm text-muted-foreground truncate">
                  {isDatabaseDuplicate ? t("bulk.duplicate.fromDatabase") : t("bulk.duplicate.earlierInBatch")}
                </span>
              </div>

              {isDatabaseDuplicate && displayExistingOrder ? (
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">{t("bulk.duplicate.orderNumber")}</span> {displayExistingOrder.order_number || 'N/A'}
                  </div>
                  <div>
                    <span className="font-medium">{t("bulk.duplicate.customer")}</span> {displayExistingOrder.customer_name || 'N/A'}
                  </div>
                  <div>
                    <span className="font-medium">{t("bulk.duplicate.phone")}</span> {displayExistingOrder.customer_phone || 'N/A'}
                  </div>
                  <div>
                    <span className="font-medium">{t("bulk.duplicate.total")}</span> {displayExistingOrder.total_price ? `${displayExistingOrder.total_price}₾` : 'N/A'}
                  </div>
                  <div>
                    <span className="font-medium">{t("bulk.duplicate.products")}</span>
                    <ul className="mt-1 space-y-1 ml-4">
                      {displayExistingOrder.parsed_items && JSON.parse(displayExistingOrder.parsed_items).map((p: any, idx: number) => (
                        <li key={idx} className="text-muted-foreground">
                          • {p.name} {p.quantity && `(${p.quantity})`}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t("bulk.duplicate.created")} {displayExistingOrder.created_at ? new Date(displayExistingOrder.created_at).toLocaleString() : 'N/A'}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                  <p className="text-center">
                    {t("bulk.duplicate.earlierInBatch")}<br />
                    {t("bulk.duplicate.checkRow")} {newOrder.duplicateOf?.replace('batch-', '')}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-3 text-sm">
            <p className="font-medium text-yellow-900 dark:text-yellow-100">
              ⚠️ {t("bulk.duplicate.detectionWarning")}
            </p>
            <p className="text-yellow-700 dark:text-yellow-300 mt-1">
              {newOrder.duplicateReason || t("bulk.duplicate.detectionWarning")}
            </p>
          </div>
        </ScrollArea>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
            {t("bulk.duplicate.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
